import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { WelcomeMessage } from '@aralere/shared-types';
import { useAuth } from '../AuthProvider.js';
import { useBrand } from '../BrandProvider.js';
import { api } from '../api-client.js';
import { Button } from './Button.js';

// Welcome modal (spec §6.3, slice 5). A must-close, once-per-user-per-brand modal rendered by
// AppShell. The brand's welcomeMessage (headline/body/videoUrl) comes from /api/me (PublicBrand);
// whether THIS user has already closed it is a receipt-backed flag from /api/welcome (cross-device).
// Closing (button or backdrop) marks it seen so it won't reappear; the Account-settings "Show again"
// replay resets that flag. A toast/notification-bell item this is NOT — it's a deliberate, blocking
// first-run greeting.

// Extract a YouTube video id from the common URL shapes (watch?v=, youtu.be/, /embed/, /shorts/).
function youTubeId(url: string): string | null {
  const m =
    /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/.exec(url);
  return m ? m[1] : null;
}
// Extract a numeric Vimeo id from vimeo.com/<id>.
function vimeoId(url: string): string | null {
  const m = /vimeo\.com\/(?:video\/)?(\d+)/.exec(url);
  return m ? m[1] : null;
}

function VideoBlock({ url }: { url: string }): JSX.Element | null {
  const u = url.trim();
  if (!u) return null;
  const yt = youTubeId(u);
  if (yt) {
    return (
      <div className="aspect-video w-full overflow-hidden rounded-lg bg-[rgb(var(--edge-rgb)/0.05)]">
        <iframe
          className="w-full h-full"
          src={`https://www.youtube-nocookie.com/embed/${yt}`}
          title="Welcome video"
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }
  const vm = vimeoId(u);
  if (vm) {
    return (
      <div className="aspect-video w-full overflow-hidden rounded-lg bg-[rgb(var(--edge-rgb)/0.05)]">
        <iframe
          className="w-full h-full"
          src={`https://player.vimeo.com/video/${vm}`}
          title="Welcome video"
          allow="fullscreen; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }
  if (/\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i.test(u)) {
    return <video className="w-full rounded-lg bg-[rgb(var(--edge-rgb)/0.05)]" src={u} controls preload="metadata" />;
  }
  // Unknown host — don't iframe arbitrary URLs; offer a safe link instead.
  return (
    <a
      href={u}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex text-sm font-medium text-[var(--link-text)] underline"
    >
      Watch the video →
    </a>
  );
}

// Past any scheduled start? Missing/empty/invalid startsAt = active immediately.
function isStarted(wm: WelcomeMessage | { enabled: false } | undefined): boolean {
  const startsAt = wm && 'startsAt' in wm ? wm.startsAt : null;
  if (!startsAt) return true;
  const t = Date.parse(startsAt);
  return Number.isNaN(t) || Date.now() >= t;
}

export function WelcomeModal(): JSX.Element | null {
  const { user } = useAuth();
  const { brand } = useBrand();
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);

  const wm = brand?.welcomeMessage;
  // Active = enabled AND past any scheduled start. welcomeMessage on PublicBrand is
  // `WelcomeMessage | { enabled: false }`; `enabled` is the common field, so this is safe before
  // narrowing.
  const enabled = !!wm && wm.enabled && isStarted(wm);

  // On sign-in, ask the server whether this user has already closed this brand's welcome. Only fire
  // when enabled (most loads skip the call). Best-effort: never block or error the shell.
  useEffect(() => {
    if (!user || !enabled) return;
    let alive = true;
    void api.welcome.status().then(
      (r) => {
        if (alive && !r.seen) setOpen(true);
      },
      () => {
        /* on error, don't show — a greeting is not worth a failure mode */
      }
    );
    return () => {
      alive = false;
    };
  }, [user, enabled]);

  // `'headline' in wm` narrows the union to WelcomeMessage (the disabled member has only `enabled`).
  if (!open || !wm || !('headline' in wm)) return null;

  const close = (): void => {
    if (closing) return;
    setClosing(true);
    // Persist "seen" then close regardless of the result (a failed mark just means it may reappear).
    void api.welcome.markSeen().finally(() => {
      setOpen(false);
      setClosing(false);
    });
  };

  const videoUrl = wm.videoUrl ?? null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={wm.headline || 'Welcome'}
    >
      <div className="absolute inset-0 glass-scrim" onClick={close} aria-hidden="true" />
      <div className="relative w-full max-w-lg rounded-xl border border-[rgb(var(--edge-rgb)/0.10)] bg-[var(--surface-solid)] shadow-xl p-7 space-y-5 max-h-[90vh] overflow-y-auto">
        {wm.headline ? (
          <h2 className="font-display text-2xl tracking-tight text-[var(--color-text)]">{wm.headline}</h2>
        ) : null}
        {videoUrl ? <VideoBlock url={videoUrl} /> : null}
        {wm.body ? (
          <p className="text-sm text-[var(--color-text)] leading-relaxed whitespace-pre-line">{wm.body}</p>
        ) : null}
        <div className="flex justify-end pt-1">
          <Button type="button" onClick={close} loading={closing}>
            Get started
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
